import React, { useRef, useState } from 'react';

import { CursorTooltipProps, TooltipData } from './CursorTooltip.types';
import { TooltipContent } from './TooltipContent';
import { useTooltipElements } from './useTooltipElements';
import { useTooltipEvents } from './useTooltipEvents';
import { useTooltipState } from './useTooltipState';

export * from './CursorTooltip.types';

/**
 * CursorTooltip - cursor-following tooltip component
 *
 * @component
 *
 * @param {React.RefObject<HTMLDivElement>} containerRef - Reference to the container element that contains tooltip targets
 * @param {(isVisible: boolean) => void} [onVisibilityChange] - Callback fired when tooltip visibility changes (useful for cursor styling)
 * @param {(tooltipId: string | null) => void} [onActiveTooltipChange] - Callback fired when the active tooltip changes, receives the `data-tooltip-id` value or null
 *
 * @remarks
 * ** Usage:**
 * 1. Include the component
 * 2. Add `data-tooltip-title` and `data-tooltip-content` attributes to the elements that need toshow the tooltip.
 * 3. Add `data-tooltip-id` attribute if you intend to use `onActiveTooltipChange` callbacks.
 */
export const CursorTooltip: React.FC<CursorTooltipProps> = ({
  containerRef,
  onVisibilityChange,
  onActiveTooltipChange,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipElementsRef = useTooltipElements(containerRef);

  const [isVisible, setIsVisible] = useState(false);
  const [tooltipData, setTooltipData] = useState<TooltipData>({
    title: '',
    content: '',
  });

  const { updateTooltipState, positionRef } = useTooltipState({
    containerRef,
    tooltipRef,
    tooltipElementsRef,
    onVisibilityChange,
    onActiveTooltipChange,
    setIsVisible,
    setTooltipData,
    isVisible,
  });

  useTooltipEvents({
    updateTooltipState,
  });

  return (
    <TooltipContent
      tooltipRef={tooltipRef}
      isVisible={isVisible}
      position={positionRef.current}
      tooltipData={tooltipData}
    />
  );
};
